<script>
  import { onMount, onDestroy } from 'svelte';
  import SurveyContainer from './components/Survey/SurveyContainer.svelte';
  import { initializeSurvey } from './stores/surveyStore';

  // Check if we're in embedded mode
  export let embedded = false;

  let isLoaded = false;
  let intervalId = null;

  // Improved function to send height updates to the parent frame
  function updateParentHeight() {
    // Get the actual content height using multiple methods for accuracy
    const height = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
    
    console.log('Sending height update to parent:', height);
    
    // Send message to parent frame with wildcard target (more reliable)
    window.parent.postMessage({
      type: 'resize',
      height: height
    }, '*');
  }

  onMount(() => {
    // Initialize the survey data
    initializeSurvey();

    // Check if we're running in an iframe
    if (window.self !== window.top) {
      console.log('Running in embedded mode');
      embedded = true;
      
      // Apply styles to the document body
      try {
        document.body.style.background = 'transparent';
        document.body.style.margin = '0';
        document.body.style.padding = '0';
        document.body.style.overflow = 'visible';
      } catch (e) {
        console.error('Could not modify styles', e);
      }

      // Set the component to loaded state immediately to avoid opacity issues
      isLoaded = true;

      // More aggressive height updates during initialization
      updateParentHeight(); // Initial call
      setTimeout(updateParentHeight, 100);
      setTimeout(updateParentHeight, 500);
      setTimeout(updateParentHeight, 1000);
      
      // Then set up regular updates
      window.addEventListener('resize', updateParentHeight);
      document.addEventListener('DOMContentLoaded', updateParentHeight);
      
      // Set up interval for continuous updates
      intervalId = setInterval(updateParentHeight, 2000);
      
      // Add listeners for content changes that might affect height
      const observer = new MutationObserver(updateParentHeight);
      observer.observe(document.body, { 
        childList: true, 
        subtree: true,
        attributes: true 
      });
    } else {
      // Only add the short delay for non-embedded mode
      setTimeout(() => {
        isLoaded = true;
      }, 300);
    }
  });

  onDestroy(() => {
    if (embedded) {
      window.removeEventListener('resize', updateParentHeight);
      document.removeEventListener('DOMContentLoaded', updateParentHeight);
      if (intervalId) {
        clearInterval(intervalId);
      }
    }
  });
</script>

<main class="app-container {isLoaded ? 'loaded' : ''} {embedded ? 'embedded' : ''}">
  <SurveyContainer embedded={embedded} />
</main>

<style>
  :global(body) {
    margin: 0;
    padding: 0;
    font-family: "Hoboken High Sans Serif", sans-serif;
    background-color: transparent;
    color: #333;
    overflow-x: hidden;
  }
  
  :global(*) {
    box-sizing: border-box;
  }
  
  .app-container {
    display: flex;
    flex-direction: column;
    min-height: 500px; /* More reasonable minimum */
    opacity: 1; /* Start visible to avoid flicker */
    transition: opacity 0.3s ease-in-out;
  }
  
  .loaded {
    opacity: 1;
  }
  
  .embedded {
    min-height: 500px; /* Keep minimum height when embedded */
    height: auto; /* Let it grow based on content */
    overflow: visible; /* Show all content */
    background: transparent;
    box-shadow: none;
    border-radius: 0;
    margin: 0;
    padding: 0;
  }
</style>